import { ChangeEvent } from "react";
import { network } from "../../../../config";
import { siteLangs } from "../../../../constans/siteLangs";
import Input from "../../../../components/Input/Input";
import TextEditor from "../../../../components/TextEditor/TextEditor";
import url from "../../../../utils/formApiUrl";
import { getTranslation } from "../../../../utils/getTranslation";
import { ProjectFormController } from "../useProjectFormController";
import s from "../ProjectFormPage.module.css";

const connectionString = process.env.REACT_APP_HTTP_CONNECTION_STRING;

type ContentStepProps = {
  controller: ProjectFormController;
  selectedLanguage: string;
  onLanguageChange: (language: string) => void;
};

const ContentStep = ({
  controller,
  selectedLanguage,
  onLanguageChange,
}: ContentStepProps) => {
  const selectedVideoFile = controller.videos.find(
    (item) => item.language === selectedLanguage,
  )?.video;
  const selectedExistingVideo = controller.existingVideos?.find(
    (item) => item.language === selectedLanguage,
  );
  const selectedVideoSource = selectedVideoFile
    ? URL.createObjectURL(selectedVideoFile)
    : selectedExistingVideo?.link?.length
      ? selectedExistingVideo.link
      : selectedExistingVideo?.video?.length
        ? url(
            `${connectionString}projects/`,
            network.projects.getProjectVideoStream,
            { id: selectedExistingVideo.video },
          )
        : undefined;
  const hasSelectedVideo = Boolean(selectedVideoFile || selectedVideoSource);

  const updateTranslatedField = (
    field: "title" | "description" | "briefDescription" | "adminRemarkForExperts",
    value: string,
  ) => {
    controller.setProjectData((prev) => ({
      ...prev,
      [field]: prev[field]!.map((item) =>
        item.language === selectedLanguage ? { ...item, text: value } : item,
      ),
    }));
  };

  const handleVideoChange = (event: ChangeEvent<HTMLInputElement>) => {
    controller.setVideoForLanguage(
      selectedLanguage,
      event.target.files?.[0] || undefined,
    );
  };

  return (
    <div className={s.stepPanel}>
      <div className={s.section}>
        <h2 className={s.sectionTitle}>Content</h2>
        <p className={s.sectionText}>
          Manage the Project copy and intro video for each supported language.
        </p>
      </div>

      <div className={s.languageTabs}>
        {siteLangs.map((item) => (
          <button
            key={item.value}
            type="button"
            className={`${s.languageButton} ${
              selectedLanguage === item.value ? s.activeLanguageButton : ""
            }`}
            onClick={() => onLanguageChange(item.value)}
          >
            {item.lable}
          </button>
        ))}
      </div>

      <div className={s.fieldBlock}>
        <label className={s.fieldLabel} htmlFor="project-title-input">
          Project title
        </label>
        <Input
          inputValue={getTranslation(
            controller.projectData.title,
            selectedLanguage,
            true,
          )}
          onChangeInput={(value) => updateTranslatedField("title", value)}
          isVisible
          required
          ariaLabel="Project title"
        />
      </div>

      <div className={s.fieldBlock}>
        <span className={s.fieldLabel}>Intro video</span>
        {hasSelectedVideo ? (
          <div className={s.videoBlock}>
            <video
              src={selectedVideoSource}
              className={s.video}
              controls
            />
            <label className={s.fileInputBlock}>
              <input
                aria-label="Project intro video"
                style={{ display: "none" }}
                type="file"
                accept="video/*"
                onChange={handleVideoChange}
              />
              <span className={s.uploadVideoBtn}>Change video</span>
            </label>
          </div>
        ) : (
          <label className={s.fileInputBlock}>
            <input
              aria-label="Project intro video"
              style={{ display: "none" }}
              type="file"
              accept="video/*"
              onChange={handleVideoChange}
            />
            <span className={s.uploadVideoBtn}>Upload video</span>
          </label>
        )}
      </div>

      <div className={s.fieldBlock}>
        <span className={s.fieldLabel}>Project description</span>
        <TextEditor
          value={getTranslation(
            controller.projectData.description,
            selectedLanguage,
            true,
          )}
          onChange={(value) => updateTranslatedField("description", value)}
        />
      </div>

      <div className={s.fieldBlock}>
        <span className={s.fieldLabel}>Brief description</span>
        <textarea
          className={s.textarea}
          value={getTranslation(
            controller.projectData.briefDescription,
            selectedLanguage,
            true,
          )}
          onChange={(event) =>
            updateTranslatedField("briefDescription", event.target.value)
          }
        />
      </div>

      <div className={s.fieldBlock}>
        <span className={s.fieldLabel}>Administrator note for experts</span>
        <textarea
          className={s.textarea}
          value={getTranslation(
            controller.projectData.adminRemarkForExperts,
            selectedLanguage,
            true,
          )}
          onChange={(event) =>
            updateTranslatedField("adminRemarkForExperts", event.target.value)
          }
        />
      </div>
    </div>
  );
};

export default ContentStep;
